import { ONE_MINUTE_MS, ONE_SECOND } from '../../../../../pure-js/libs/Consts'

// Tunables for the assistant surface. Anything a reader would otherwise meet as a bare number.

/** Long enough to read a phrase, short enough that a slow answer still feels alive. */
export const LOADING_PHRASE_ROTATION_MS = 1450 // ~1.5 s

/** Keeps the input from composing a message too long to be answered. */
export const MAX_CHAT_INPUT_LENGTH = 600

/** Keeps one conversation from raising request after request. */
export const MAX_ESCALATIONS_PER_CONVERSATION = 2

/** How much of the conversation is quoted into a developer escalation. */
export const ESCALATION_SUMMARY_MESSAGE_COUNT = 6
export const MAX_ESCALATION_SUMMARY_LENGTH = 900

/**
 * The assistant is a floating action surface, so it sits at the FAB layer — deliberately below the
 * Whereby call card (1200) and its minimized button (1300). Both are draggable and can be moved on top
 * of the launcher, and a live call must always win that overlap.
 */
export const ASSISTANT_Z_INDEX_LAYER = 'fab' as const

/** How far the widget sits from the right and the bottom of the screen in its default corner. */
export const LAUNCHER_RIGHT_INSET_PX = { xs: 16, sm: 24, md: 24 }
export const LAUNCHER_BOTTOM_INSET_PX = { xs: 18, sm: 24, md: 24 }

/** Her greeting beside the bubble: a couple of lines, never a panel. */
export const GREETING_MAX_WIDTH_PX = 280
export const GREETING_DISMISS_INSET_PX = 6
export const GREETING_ENTER_MS = 320 // ~0.3 s

/** The wait before trying again after a failed save of what was said; it grows with each failure in a row. */
export const SAVE_RETRY_FIRST_DELAY_MS = 10 * ONE_SECOND // 10 s
export const SAVE_RETRY_BACKOFF_FACTOR = 2 // doubles after each failure
/** The longest wait between two tries, so what was said is still saved once a longer outage ends. */
export const SAVE_RETRY_MAX_DELAY_MS = 3 * ONE_MINUTE_MS // 3 min

/** Chat panel geometry. Deliberately compact — it floats over the page the user is working on. */
export const PANEL_WIDTH_PX = 320
export const PANEL_HEIGHT_PX = 496
/** Folded in by the side rail: narrow enough to sit out of the way, wide enough to still read a reply. */
export const PANEL_COMPACT_WIDTH_PX = 216
/** How far the hover rail sits outside the panel's left edge. */
export const PANEL_SIDE_RAIL_OUTSET_PX = 15
/** How far below the top of the conversation the rail sits, whatever rows are above it. */
export const PANEL_SIDE_RAIL_INSET_PX = 7
/** Room left for the launcher and the viewport edge when the panel goes full-width on mobile. */
export const PANEL_VIEWPORT_INSET_PX = 32
export const PANEL_TOP_INSET_PX = 56
/** On a phone the panel fills the screen, up to this tall. */
export const PANEL_MOBILE_MAX_HEIGHT_PX = 640
export const PANEL_WIDTH_TRANSITION_MS = 180 // 0.18 s
export const PANEL_SIDE_RAIL_FADE_MS = 160 // 0.16 s
/** A thin scrollbar, so the conversation keeps its width. */
export const CONVERSATION_SCROLLBAR_WIDTH_PX = 6

/** Quick-action chip sizing — scaled to the panel width above; the 44px min height is the tap target. */
export const QUICK_ACTION_ROW_MAX_WIDTH_PX = 264
export const QUICK_ACTION_COLUMN_MAX_HEIGHT_PX = 180
export const QUICK_ACTION_CHIP_MIN_WIDTH_PX = 106
export const QUICK_ACTION_CHIP_MAX_WIDTH_PX = 133
export const QUICK_ACTION_CHIP_MIN_HEIGHT_PX = 44
export const QUICK_ACTION_CHIP_COMPACT_HEIGHT_PX = 28

/** Voice-only: nothing else is on screen, so the bulb's glow gets room to fall off. */
export const PANEL_BULB_SIZE_PX = 145
/** With captions on, the bulb yields the panel to the conversation rather than the other way round. */
export const PANEL_BULB_SIZE_WITH_SUBTITLES_PX = 79
export const VOICE_CONTROL_BUTTON_SIZE_PX = 38
export const COMPOSER_SEND_BUTTON_SIZE_PX = 40
/** Folded, a smaller Send leaves the input room for its whole placeholder. */
export const COMPOSER_SEND_BUTTON_COMPACT_SIZE_PX = 32
export const COMPOSER_SEND_ICON_SIZE_PX = 20
/** The panel card's MUI shadow level: the highest, as it floats over whatever page is open. */
export const PANEL_ELEVATION = 20

export const MESSAGE_MAX_WIDTH = '88%'

/** How long Aria stays on screen after landing where she is not allowed, so she can lift away. */
export const ASSISTANT_FAREWELL_MS = 620 // ~0.6 s
